import React, { FC, useEffect, useMemo } from 'react';
import Modal from 'components/Modal/Modal';
import useForm from 'hooks/useForm';
import { ModalWidthEnum } from 'components/Modal/Modal.types';
import { Moment } from 'moment';
import CreateTimeEntryForm from 'pages/private/Timesheet/components/CreateTimeEntry/CreateTimeEntryForm';
import { TCreateTimeEntryInitialValues } from 'pages/private/Timesheet/components/CreateTimeEntry/CreateTimeEntryForm.type';
import { AssignmentEnumUi, TTimeEntryPostUi } from 'data/types/timeEntry.types';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { setRedirectWorkfeed } from 'redux/slices';
import { useNavigate } from 'react-router-dom';
import { RoutesPaths } from 'routes/Routes.types';
import { projectApi } from 'redux/apiSlices/project.slice';
import { useUserTimezone } from 'hooks/useGetDateWithTimeZone';
import { getNowInTimezone } from 'helpers/timezoneUtils';

const TimeEntryFormModal: FC<{
  visible: boolean;
  onClose: () => void;
  isSuccess: boolean;
  isLoading: boolean;
  reset: () => void;
  selectedDates?: { startTime: Moment; endTime: Moment } | null;
  onSubmit: (values: TTimeEntryPostUi) => Promise<void>;
}> = ({
  visible,
  onClose,
  selectedDates = null,
  reset,
  isLoading,
  isSuccess,
  onSubmit,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { abbreviation, timeZone } = useUserTimezone();

  // ZEST-581: Get current time in user's timezone for default values
  // Recompute when modal opens (visible) to ensure fresh time
  const nowInUserTimezone = useMemo(
    () => getNowInTimezone(timeZone),
    [timeZone, visible],
  );

  const shouldRedirectWorkfeed = useAppSelector(
    state => state.appSettings.redirectWorkfeed,
  );
  const { form, setCustomFieldValue } = useForm();
  const { setFieldsValue } = form;
  const currentAssignment = useAppSelector(
    state => state.appSettings.currentAssignment,
  );
  // ZEST-581: Use user timezone for default start/end times when no dates selected
  const defaultStartTime = useMemo(() => {
    if (selectedDates) return selectedDates.startTime;
    // Default to current time in user's timezone
    return nowInUserTimezone.clone();
  }, [selectedDates, nowInUserTimezone]);

  const defaultEndTime = useMemo(() => {
    if (selectedDates) return selectedDates.endTime;
    // Default to 1 hour after start time in user's timezone
    return nowInUserTimezone.clone().add(1, 'hour');
  }, [selectedDates, nowInUserTimezone]);

  const initialValues: TCreateTimeEntryInitialValues = {
    note: undefined,
    companyId: currentAssignment ? currentAssignment.company : undefined,
    startTime: defaultStartTime,
    endTime: defaultEndTime,
    assignmentId: currentAssignment
      ? currentAssignment.assignmentId
      : undefined,
    workType: undefined,
    assignmentType: currentAssignment
      ? currentAssignment.assignmentType
      : undefined,
  };
  const onCancel = () => {
    if (onClose) onClose();
    form.resetFields();
    reset();
    if (shouldRedirectWorkfeed) {
      navigate(`/app/${RoutesPaths.Workfeed}`);
      dispatch(setRedirectWorkfeed(false));
    }
  };
  const submit = async () => {
    try {
      const values: TTimeEntryPostUi = await form.validateFields();
      await onSubmit({
        ...values,
        note: values.note?.trim(),
      });
      if (values.assignmentType === AssignmentEnumUi['Project Task']) {
        dispatch(projectApi.util.invalidateTags(['project']));
      }
    } catch (err) {
      // Todo: toast
    }
  };

  useEffect(() => {
    if (isSuccess) {
      onCancel();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (visible) {
      setFieldsValue(initialValues);
    }
  }, [currentAssignment, visible]);

  useEffect(() => {
    if (visible) {
      // ZEST-581: Use computed defaults (profile timezone) when no selectedDates
      setFieldsValue({
        startTime: defaultStartTime,
        endTime: defaultEndTime,
      });
    }
  }, [
    selectedDates?.startTime,
    selectedDates?.endTime,
    visible,
    defaultStartTime,
    defaultEndTime,
  ]);

  return (
    <Modal
      size={ModalWidthEnum.Large}
      visible={visible}
      title={
        <span
          style={{ fontSize: 20 }}
        >{`Add time entry (${abbreviation})`}</span>
      }
      onCancel={onCancel}
      onOk={submit}
      okText="Add entry"
      confirmLoading={isLoading}
      destroyOnClose
    >
      <CreateTimeEntryForm
        formObj={{ form, setCustomFieldValue }}
        initialValues={initialValues}
        onSubmit={submit}
      />
    </Modal>
  );
};

export default TimeEntryFormModal;
